/**
 * 建立覆盖（从全局复制 / 直接创建空覆盖）与移除覆盖弹窗。
 *
 * 三者均调用 app-dedicated-configs 真端点；成功后刷新服务端 config_source。
 * 两条建立路径只在 `init_mode` 上分叉（A2 §0.4），身份键与移除语义完全相同。
 */

import type { AppDedicatedConfigModule } from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { opsApi } from '~/hooks/use-ops';
import { langName } from '~/lib/format';
import { Dialog } from '~/ui/Dialog';
import { toast } from '~/ui/Toast';
import {
  type OpsModule,
  opsCopyButtonDisabled,
  opsDirectCreateButtonDisabled,
  opsModuleLabel,
} from './ops-scope';
import { errorToast } from './use-ops-mutations';

function dedicatedModule(module: OpsModule): AppDedicatedConfigModule {
  if (
    module === 'home_sections' ||
    module === 'faq' ||
    module === 'shelf_recommendations' ||
    module === 'banner_slides'
  ) {
    return module;
  }
  throw new Error(`Module ${module} does not use app dedicated config identities`);
}

/**
 * 直接创建空覆盖（`init_mode='empty'`）—— 只建身份行，不搬任何内容。
 *
 * 落地即「已覆盖 + 0 条」，与复制后被逐条删空是同一个态。`home_sections` 闭集 3 栏禁删、
 * 无新建入口，对它直建后该 App 该语言永久 0 栏，唯一出路是移除整份恢复继承，故单独告知。
 */
export function OpsDirectCreateOverrideDialog({
  module,
  appName,
  appId,
  lang,
  languageAllowed = true,
  onClose,
}: {
  module: OpsModule;
  appName: string;
  appId: string;
  lang: string;
  languageAllowed?: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const create = useMutation({
    mutationFn: () =>
      opsApi.copyDedicatedConfig({
        module: dedicatedModule(module),
        app_id: appId,
        language: lang,
        init_mode: 'empty',
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['home-blocks'] }),
        queryClient.invalidateQueries({ queryKey: ['faqs'] }),
        queryClient.invalidateQueries({ queryKey: ['shelf-recommendations'] }),
        queryClient.invalidateQueries({ queryKey: ['banners'] }),
      ]);
    },
  });
  const homeSectionsWarning = module === 'home_sections';
  return (
    <Dialog
      width={600}
      title="直接创建空覆盖"
      subtitle={`${opsModuleLabel(module)} · ${appName} · ${lang.toUpperCase()} ${langName(lang)}`}
      icon="IconPlus"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            只建立覆盖身份行 <span className="mono">(module, app_id, language)</span>
            ，不读全局层、不写任何内容行。
          </div>
          <button type="button" className="btn" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={opsDirectCreateButtonDisabled(languageAllowed) || create.isPending}
            onClick={() =>
              create.mutate(undefined, {
                onSuccess: () => {
                  toast(`已为 ${appName} · ${lang.toUpperCase()} 创建空覆盖`, {
                    tone: 'pos',
                    icon: 'IconPlus',
                  });
                  onClose();
                },
                onError: (error) => errorToast(error, '创建空覆盖失败'),
              })
            }
          >
            创建空覆盖
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <div className="card-flat" style={{ padding: 12, fontSize: 12 }}>
          创建后该 App 在 <span className="mono">{lang}</span> 下为
          <span style={{ fontWeight: 500 }}>已覆盖 + 0 条</span> —— C 端不再回退读该语言全局，
          内容由你在本表逐条添加。
        </div>
        {homeSectionsWarning && (
          <div
            style={{
              marginTop: 12,
              padding: '10px 12px',
              borderRadius: 6,
              fontSize: 12,
              color: 'var(--warn)',
              border: '1px solid var(--warn)',
            }}
          >
            首页推荐分区标题为固定 3 栏闭集、无新建入口且禁删：对它直建后该 App 该语言
            <span style={{ fontWeight: 500 }}>永久 0 栏且无法逐条补内容</span>
            ，唯一出路是「移除覆盖」恢复继承全局。
          </div>
        )}
      </div>
    </Dialog>
  );
}

export function OpsCopyOverrideDialog({
  module,
  appName,
  appId,
  lang,
  globalN,
  languageAllowed = true,
  onClose,
}: {
  module: OpsModule;
  appName: string;
  appId: string;
  lang: string;
  globalN: number;
  languageAllowed?: boolean;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const copy = useMutation({
    mutationFn: () =>
      opsApi.copyDedicatedConfig({
        module: dedicatedModule(module),
        app_id: appId,
        language: lang,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['home-blocks'] }),
        queryClient.invalidateQueries({ queryKey: ['faqs'] }),
        queryClient.invalidateQueries({ queryKey: ['shelf-recommendations'] }),
        queryClient.invalidateQueries({ queryKey: ['banners'] }),
      ]);
    },
  });
  return (
    <Dialog
      width={600}
      title="从全局复制"
      subtitle={`${opsModuleLabel(module)} · ${appName} · ${lang.toUpperCase()} ${langName(lang)}`}
      icon="IconCopy"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            复制成功即建立覆盖身份行 <span className="mono">(module, app_id, language)</span>
            ，并在同一事务复制当前语言的全局快照。
          </div>
          <button type="button" className="btn" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-accent"
            disabled={opsCopyButtonDisabled(globalN, languageAllowed) || copy.isPending}
            onClick={() =>
              copy.mutate(undefined, {
                onSuccess: () => {
                  toast(`已复制 ${globalN} 条为 ${appName} · ${lang.toUpperCase()} 独立配置`, {
                    tone: 'pos',
                    icon: 'IconCopy',
                  });
                  onClose();
                },
                onError: (error) => errorToast(error, '复制独立配置失败'),
              })
            }
          >
            复制并创建覆盖
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <div className="card-flat" style={{ padding: 12, fontSize: 12 }}>
          复制源 = <span style={{ fontWeight: 500 }}>{lang.toUpperCase()} 全局</span>（
          <span className="mono">app_id</span> 空 + <span className="mono">language={lang}</span>）·
          共 {globalN} 条
        </div>
        <div
          style={{
            marginTop: 12,
            padding: '10px 12px',
            borderRadius: 6,
            background: 'var(--info-soft)',
            fontSize: 11.5,
            lineHeight: 1.65,
          }}
        >
          <div>· 只复制当前语言这一份：不跨语言、不跨模块、不做英语兜底。</div>
          <div>· 复制后 C 端只读覆盖；无覆盖的其它 App 仍读该语言全局。</div>
          <div>· 复制后两层解耦，差异由运营手动维护；恢复继承的唯一路径是「移除覆盖」。</div>
        </div>
      </div>
    </Dialog>
  );
}

export function OpsRemoveOverrideDialog({
  module,
  appName,
  appId,
  lang,
  overrideN,
  onClose,
}: {
  module: OpsModule;
  appName: string;
  appId: string;
  lang: string;
  overrideN: number;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const remove = useMutation({
    mutationFn: () =>
      opsApi.removeDedicatedConfig({
        module: dedicatedModule(module),
        app_id: appId,
        language: lang,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['home-blocks'] }),
        queryClient.invalidateQueries({ queryKey: ['faqs'] }),
        queryClient.invalidateQueries({ queryKey: ['shelf-recommendations'] }),
        queryClient.invalidateQueries({ queryKey: ['banners'] }),
      ]);
    },
  });
  return (
    <Dialog
      width={600}
      title="移除覆盖"
      subtitle={`整份移除：${opsModuleLabel(module)} × ${appName} × ${lang.toUpperCase()}`}
      icon="IconAlert"
      onClose={onClose}
      footer={
        <>
          <div style={{ flex: 1, fontSize: 11, color: 'var(--fg-muted)' }}>
            当前会话已通过 MFA；移除动作会写入审计日志
          </div>
          <button type="button" className="btn" onClick={onClose}>
            取消
          </button>
          <button
            type="button"
            className="btn btn-danger"
            disabled={remove.isPending}
            onClick={() =>
              remove.mutate(undefined, {
                onSuccess: () => {
                  toast(`已移除 ${appName} · ${lang.toUpperCase()} 的整份独立配置`, {
                    tone: 'warn',
                    icon: 'IconTrash',
                  });
                  onClose();
                },
                onError: (error) => errorToast(error, '移除独立配置失败'),
              })
            }
          >
            确认移除整份
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <div
          style={{
            padding: '10px 12px',
            borderRadius: 6,
            background: 'var(--neg-soft)',
            fontSize: 11.5,
            lineHeight: 1.65,
          }}
        >
          <div className="hstack" style={{ gap: 6, marginBottom: 4 }}>
            <span className="chip chip-neg" style={{ fontSize: 9.5 }}>
              危险操作
            </span>
            <span style={{ fontWeight: 500 }}>对象是整份覆盖，不是其中某一条</span>
          </div>
          <div>
            · 当前覆盖 <span className="mono">{overrideN}</span> 条将一并消失（含随其复制的子行）。
          </div>
          <div>· 移除后该 App 该语言回到「无覆盖」，C 端读该语言全局；可再次复制届时的全局。</div>
          <div>· 删覆盖里的单条 ≠ 移除覆盖，删到 0 条仍是「已覆盖」。</div>
        </div>
      </div>
    </Dialog>
  );
}
